import { transliterateToMarathi } from './transliterate';

export interface ExtractedCouple {
  groom: string;
  bride: string;
}

/**
 * Extracts groom and bride names from an event title (Marathi, Hindi, or English).
 * Examples:
 * - "चि. प्रविण आणि चि.सौ.कां. तेजस्वी यांचा विवाह" -> { groom: "प्रविण", bride: "तेजस्वी" }
 * - "Pravin & Tejaswi Wedding" -> { groom: "Pravin", bride: "Tejaswi" }
 * - "Rahul weds Sneha" -> { groom: "Rahul", bride: "Sneha" }
 * - "Wedding of Rohan and Isha" -> { groom: "Rohan", bride: "Isha" }
 * - "अभिषेक व पूजा शुभविवाह" -> { groom: "अभिषेक", bride: "पूजा" }
 */
export function extractCoupleNames(eventName?: string | null): ExtractedCouple | null {
  if (!eventName || typeof eventName !== 'string') return null;

  let clean = eventName.trim();

  // Strip leading prefixes
  clean = clean
    .replace(/^(the\s+)?wedding\s+of\s+/i, '')
    .replace(/^(the\s+)?विवाह\s+/i, '')
    .replace(/^(the\s+)?लग्न\s+/i, '');

  // Strip common event-related suffixes/keywords
  clean = clean
    .replace(/(wedding|invitation|लग्नपत्रिका|लग्नपत्रिक|लग्न|विवाह|शुभविवाह|यांचा|यांचे|सोहळा|समारंभ|कार्ड|card)/gi, '')
    .trim();

  const cleanIndividualName = (n: string): string => {
    return n
      .replace(/^(चि\.सौ\.कां\.|चि\.सौ\.कां|चि\.|चि|सौ\.|कु\.|श्री\.|श्री|ch\.|chi\.|mr\.|ms\.)\s*/gi, '')
      .replace(/\s*(यांचा|यांचे|weds|विवाह|लग्न)\s*$/gi, '')
      .trim();
  };

  // Separators ordered by specificity
  const separators = [
    /\s+(?:weds|संग|सोबत)\s+/i,
    /\s+(?:आणि|व)\s+/i,
    /\s+and\s+/i,
    /\s*&\s*/i,
    /\s*-\s*/i,
    /\s+w\/\s+/i,
  ];

  for (const sep of separators) {
    if (sep.test(clean)) {
      const parts = clean.split(sep);
      if (parts.length >= 2) {
        const groom = cleanIndividualName(parts[0]);
        const bride = cleanIndividualName(parts[1]);
        if (groom && bride) {
          return { groom, bride };
        }
      }
    }
  }

  return null;
}

/**
 * Flexible date parser that parses standard ISO dates, Indian formats (DD-MM-YYYY, DD/MM/YYYY),
 * ordinal dates ("24th Nov 2026"), Devanagari numerals ("२४ नोव्हेंबर २०२६"), and time strings.
 */
export function parseFlexibleDate(dateInput?: string | Date | null): Date | null {
  if (!dateInput) return null;
  if (dateInput instanceof Date) {
    return isNaN(dateInput.getTime()) ? null : dateInput;
  }

  if (typeof dateInput !== 'string') return null;

  let clean = dateInput.trim();
  if (!clean) return null;

  // Convert Devanagari digits (०-९) to ASCII (0-9)
  clean = clean.replace(/[०-९]/g, (d) => String('०१२३४५६७८९'.indexOf(d)));

  // Detect Marathi / Indian time-of-day indicators (sayankali/dupari/ratri = PM, sakali = AM)
  const isPm = /(?:दुपारी|सायंकाळी|संध्याकाळी|रात्री|\bpm\b)/i.test(clean);
  const isAm = /(?:सकाळी|\bam\b)/i.test(clean);

  // Extract explicit time if present: HH:MM or HH:MM:SS
  const timeMatch = clean.match(/\b(\d{1,2}):(\d{2})(?::(\d{2}))?\b/);
  let hasExplicitTime = false;
  let hours = 10;
  let minutes = 0;
  let seconds = 0;

  if (timeMatch) {
    hasExplicitTime = true;
    hours = parseInt(timeMatch[1], 10);
    minutes = parseInt(timeMatch[2], 10);
    seconds = timeMatch[3] ? parseInt(timeMatch[3], 10) : 0;

    if (isPm && hours < 12) hours += 12;
    if (isAm && hours === 12) hours = 0;
  }

  // Map Marathi/Hindi months to English
  const monthMap: Record<string, string> = {
    'जानेवारी': 'January', 'फेब्रुवारी': 'February', 'मार्च': 'March',
    'एप्रिल': 'April', 'मे': 'May', 'जून': 'June',
    'जुलै': 'July', 'ऑगस्ट': 'August', 'सप्टेंबर': 'September',
    'ऑक्टोबर': 'October', 'नोव्हेंबर': 'November', 'डिसेंबर': 'December',
  };

  for (const [mr, en] of Object.entries(monthMap)) {
    clean = clean.replace(new RegExp(mr, 'gi'), en);
  }

  // Remove Marathi weekdays, time words, and ceremony noise words
  clean = clean
    .replace(/(रविवार|सोमवार|मंगळवार|बुधवार|गुरुवार|शुक्रवार|शनिवार)/gi, '')
    .replace(/(दिनांक|दि\.|रोजी|वाजता|सायंकाळी|संध्याकाळी|दुपारी|रात्री|सकाळी)/gi, '')
    .replace(/\b(at|pm|am)\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim();

  // Strip ordinal suffixes (1st, 2nd, 3rd, 24th)
  clean = clean.replace(/\b(\d{1,2})(st|nd|rd|th)\b/gi, (_m, d) => d);

  // 1. Match YYYY-MM-DD or YYYY/MM/DD date-only strings first to prevent UTC midnight date shift
  const ymdMatch = clean.match(/^(\d{4})[./-](\d{1,2})[./-](\d{1,2})$/);
  if (ymdMatch) {
    const [, year, month, day] = ymdMatch;
    // Anchor to noon (12:00) so local timezone differences never roll the calendar day over
    const constructed = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10), hasExplicitTime ? hours : 12, minutes, seconds);
    if (!isNaN(constructed.getTime())) {
      return constructed;
    }
  }

  // 2. Try standard Date parsing
  const std = new Date(clean);
  if (!isNaN(std.getTime())) {
    const isIsoWithTime = clean.includes('T') || /Z$/i.test(clean) || /[+-]\d{2}:?\d{2}$/.test(clean);
    if (hasExplicitTime && !isIsoWithTime) {
      std.setHours(hours, minutes, seconds, 0);
    }
    return std;
  }

  // 3. Match DD-MM-YYYY or DD/MM/YYYY or DD.MM.YYYY
  const dmyMatch = clean.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})/);
  if (dmyMatch) {
    const [, day, month, year] = dmyMatch;
    const constructed = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10), hasExplicitTime ? hours : 12, minutes, seconds);
    if (!isNaN(constructed.getTime())) {
      return constructed;
    }
  }

  // 4. Match textual month with day & year: e.g. "18 September 2026" or "September 18 2026"
  const textDateMatch = clean.match(/\b(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})\b/) ||
                        clean.match(/\b([A-Za-z]+)\s+(\d{1,2}),?\s+(\d{4})\b/);
  if (textDateMatch) {
    let dayStr = textDateMatch[1];
    let monthStr = textDateMatch[2];
    const yearStr = textDateMatch[3];
    if (isNaN(parseInt(dayStr, 10))) {
      monthStr = textDateMatch[1];
      dayStr = textDateMatch[2];
    }
    const temp = new Date(monthStr + ' 1, 2000');
    if (!isNaN(temp.getTime())) {
      return new Date(parseInt(yearStr, 10), temp.getMonth(), parseInt(dayStr, 10), hasExplicitTime ? hours : 12, minutes, seconds);
    }
  }

  return null;
}

/**
 * Safely extracts ceremonies array from wedding customData, strictly validating Array.isArray.
 * Falls back to generateDynamicCeremonies when ceremonies is absent, empty, or non-array.
 */
export function safeExtractCeremonies(
  customData?: any,
  fallbackBaseDate: Date | null = null,
  muhurtTime = "10:48 AM"
) {
  const wedding = customData?.wedding || customData;
  const rawCeremonies = wedding?.ceremonies || wedding?.weddingEvents;

  if (Array.isArray(rawCeremonies) && rawCeremonies.length > 0) {
    const tones = ['haldi', 'peacock', 'marigold', 'maroon', 'kumkum'];
    return rawCeremonies.map((c: any, idx: number) => {
      const tone = c.tone || tones[idx % tones.length];
      return {
        key: c.key || `evt-${idx}`,
        name: c.nameEn || c.name || c.title || 'Event',
        nameEn: c.nameEn || c.name || c.title || 'Event',
        nameMr: c.nameMr || c.titleMr || c.name || c.title || 'कार्यक्रम',
        mr: c.nameMr || c.titleMr || c.name || c.title || 'कार्यक्रम',
        tone,
        time: c.time || c.startTime || '12:45 PM',
        date: c.date,
        note: c.note || c.description || c.venue || '',
        description: c.note || c.description || '',
        venue: c.venue,
        coverImageUrl: c.coverImageUrl,
      };
    });
  }

  return generateDynamicCeremonies(fallbackBaseDate, muhurtTime);
}

/**
 * Formats ceremony dates anchored dynamically to the wedding date.
 * E.g., if wedding is Saturday 24 Nov 2026:
 * - Haldi: Friday 23 Nov · 9:00 AM (or Saturday 24 Nov · 8:00 AM)
 * - Mehendi: Friday 23 Nov · 4:00 PM
 * - Sangeet: Friday 23 Nov · 7:30 PM
 * - Lagna: Saturday 24 Nov · 10:48 AM
 * - Reception: Saturday 24 Nov · 7:00 PM
 */
export function generateDynamicCeremonies(baseDate: Date | null, muhurtTime = "10:48 AM") {
  const effectiveDate = baseDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000); // 30 days ahead if none
  const prevDate = new Date(effectiveDate.getTime() - 24 * 60 * 60 * 1000);

  const formatShort = (d: Date, timeStr: string) => {
    const weekday = d.toLocaleDateString('en-US', { weekday: 'short' });
    const day = d.getDate();
    const month = d.toLocaleDateString('en-US', { month: 'short' });
    return `${weekday} · ${day} ${month} · ${timeStr}`;
  };

  return [
    {
      key: "haldi",
      name: "Haldi",
      nameEn: "Haldi",
      mr: "हळद",
      nameMr: "हळद",
      tone: "haldi",
      time: formatShort(prevDate, "9:00 AM"),
      note: "Turmeric & blessings. Wear yellow.",
    },
    {
      key: "mehendi",
      name: "Mehendi",
      nameEn: "Mehendi",
      mr: "मेंदी",
      nameMr: "मेंदी",
      tone: "peacock",
      time: formatShort(prevDate, "4:00 PM"),
      note: "Henna, music & celebration.",
    },
    {
      key: "sangeet",
      name: "Sangeet",
      nameEn: "Sangeet",
      mr: "संगीत",
      nameMr: "संगीत",
      tone: "marigold",
      time: formatShort(prevDate, "7:30 PM"),
      note: "An evening of dance & music.",
    },
    {
      key: "lagna",
      name: "Lagna",
      nameEn: "Lagna",
      mr: "विवाह",
      nameMr: "विवाह",
      tone: "maroon",
      time: formatShort(effectiveDate, muhurtTime),
      note: "The wedding muhurt. Antarpat falls.",
    },
    {
      key: "reception",
      name: "Reception",
      nameEn: "Reception",
      mr: "स्वागत",
      nameMr: "स्वागत",
      tone: "kumkum",
      time: formatShort(effectiveDate, "7:00 PM"),
      note: "Dinner & blessings for the couple.",
    },
  ];
}

/**
 * Safely formats any date string, Date object, or timestamp into a localized long date string.
 * Anchors to event venue timezone (defaults to 'Asia/Kolkata' / IST) so international viewers
 * see the exact event date without timezone calendar drift.
 * Never throws RangeError. Returns fallback when date is invalid or absent.
 */
export function safeFormatDateLong(
  dateInput?: string | Date | null,
  locale = 'en-IN',
  fallback = 'Date to be announced',
  timeZone = 'Asia/Kolkata'
): string {
  const d = parseFlexibleDate(dateInput);
  if (!d) return fallback;
  try {
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    };
    if (timeZone) {
      options.timeZone = timeZone;
    }
    return new Intl.DateTimeFormat(locale, options).format(d);
  } catch {
    try {
      return new Intl.DateTimeFormat(locale, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }).format(d);
    } catch {
      return fallback;
    }
  }
}

/**
 * Safely formats any time or date string into a 12-hour formatted time (e.g. "7:00 PM").
 * Anchored to event venue timezone. Never throws RangeError.
 */
export function safeFormatTime(
  dateOrTimeInput?: string | Date | null,
  locale = 'en-IN',
  fallback = '',
  timeZone = 'Asia/Kolkata'
): string {
  if (!dateOrTimeInput) return fallback;
  if (typeof dateOrTimeInput === 'string') {
    const trimmed = dateOrTimeInput.trim();
    if (/^\d{1,2}:\d{2}(\s*(?:am|pm|वाजता))?$/i.test(trimmed)) {
      return trimmed;
    }
  }
  const d = parseFlexibleDate(dateOrTimeInput);
  if (!d) return typeof dateOrTimeInput === 'string' ? dateOrTimeInput : fallback;
  try {
    const options: Intl.DateTimeFormatOptions = {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    };
    if (timeZone) {
      options.timeZone = timeZone;
    }
    return new Intl.DateTimeFormat(locale, options).format(d);
  } catch {
    try {
      return new Intl.DateTimeFormat(locale, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      }).format(d);
    } catch {
      return fallback;
    }
  }
}

